import React from 'react';
import { useSocialContext } from './social';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const JWT_KEY = 'drupi_social_jwt';

const authHeaders = (): Record<string, string> => {
  const jwt = localStorage.getItem(JWT_KEY);
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
};

export interface PaymentProfile {
  full_name: string;
  email: string;
  paypal_account: string;
  mercadopago_alias: string;
  terms_accepted: boolean;
  /** true = tiene nombre + email válido + aceptó términos (lo calcula el backend). El alias
   *  de Mercado Pago NO es requisito (es opcional). */
  complete: boolean;
}

export type PaymentProfileFields = Partial<
  Pick<PaymentProfile, 'full_name' | 'email' | 'paypal_account' | 'mercadopago_alias' | 'terms_accepted'>
>;

interface PaymentProfileState {
  profile: PaymentProfile | null;
  loading: boolean;
  complete: boolean;
  save: (fields: PaymentProfileFields) => Promise<PaymentProfile>;
}

const Ctx = React.createContext<PaymentProfileState>({
  profile: null,
  loading: true,
  complete: false,
  save: async () => { throw new Error('PaymentProfileProvider ausente'); },
});

export const PaymentProfileProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isAuthenticated } = useSocialContext();
  const [profile, setProfile] = React.useState<PaymentProfile | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!isAuthenticated) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    fetch(`${API_BASE}/drupi/user-profile`, { headers: authHeaders() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setProfile(d); })
      .catch(() => { /* best-effort: sin perfil, el gate lo pide */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isAuthenticated]);

  const save = React.useCallback(async (fields: PaymentProfileFields) => {
    const r = await fetch(`${API_BASE}/drupi/user-profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(fields),
    });
    if (!r.ok) throw new Error('No se pudieron guardar los datos');
    const d: PaymentProfile = await r.json();
    setProfile(d);
    return d;
  }, []);

  return (
    <Ctx.Provider value={{ profile, loading, complete: !!profile?.complete, save }}>
      {children}
    </Ctx.Provider>
  );
};

export const usePaymentProfile = () => React.useContext(Ctx);
